import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
  DataGridPro,
  Toolbar,
  GridLogicOperator,
  getGridDefaultColumnTypes,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarExportButton,
  GridToolbarFilterButton,
  toolbarIconGroupSx,
} from '../utils/gridToolbarParts.js';
import { CollapsibleQuickFilter } from '../utils/CustomQuickFilter.js';
import YearTabsSelector from '../../common/YearTabsSelector.js';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import { getUserNameById } from '../../UserContext';
import CopyableIdCell from '../../../common/CopyableIdCell.js';
import { COUNTY_CODES } from '../../../common/mappings.js';
import { scarIndexSortComparator } from '../../../utils/scarIndexSort.js';
import { getTaxYearOptions } from '../../../common/helper_functions.js';
import {
  NegotiatorAnalysisCustomFiltersPanel,
  deriveNegotiatorAnalysisCustomQuery,
  newNegotiatorAnalysisCustomFilterRow,
  negotiatorAnalysisCustomFiltersNeedToolbarClear,
} from './NegotiatorAnalysisCustomFilters.js';

const COUNTY_SINGLE_SELECT_OPTIONS = Object.entries(COUNTY_CODES)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

const DEFAULT_STAGE = 'SCAR';
const STAGE_OPTIONS = [
  { value: 'BAR', label: 'BAR' },
  { value: 'SCAR', label: 'SCAR' },
  { value: 'ART7', label: 'Art 7' },
];
const STAGE_HAS_HEARING = new Set(['SCAR', 'ART7']);

const DEFAULT_GROUP_BY = 'county';
const GROUP_BY_OPTIONS = [
  { value: 'county', label: 'County' },
  { value: 'municipality', label: 'Municipality' },
  { value: 'negotiator', label: 'Negotiator' },
  { value: 'hearing_date', label: 'Court Date' },
  { value: 'hearing_officer', label: 'Hearing Officer' },
  { value: 'individual', label: 'Individual' },
];

const GROUP_BY_HEADER_NAMES = {
  county: 'County',
  municipality: 'Municipality',
  negotiator: 'Negotiator',
  hearing_officer: 'Hearing Officer',
  hearing_date: 'Court Date',
};

const DRILL_DOWN_TO_MUNICIPALITY_GROUP_BY = new Set(['negotiator', 'hearing_officer', 'hearing_date']);
function getGroupByLabel(groupByValue) {
  return GROUP_BY_OPTIONS.find((o) => o.value === groupByValue)?.label
    ?? GROUP_BY_HEADER_NAMES[groupByValue]
    ?? groupByValue;
}

function formatSubfilterDisplay(groupByValue, value) {
  if (value == null || value === '' || value === '_') {
    return groupByValue === 'negotiator' ? 'Unassigned' : 'Unknown';
  }
  if (groupByValue === 'negotiator') return getUserNameById(value) ?? String(value);
  if (groupByValue === 'hearing_date') return formatDateMmDdYy(value) || String(value);
  if (groupByValue === 'county') return COUNTY_CODES[String(value).slice(0, 1)] ?? String(value);
  return String(value);
}

function getDrillDownRootGroupBy(groupBy, subfilter) {
  if (!subfilter?.length) return groupBy;
  const first = subfilter[0].groupBy;
  if (DRILL_DOWN_TO_MUNICIPALITY_GROUP_BY.has(first)) return first;
  return groupBy;
}

function buildDrillDownBreadcrumbs({ groupBy, subfilter }) {
  const rootGroupBy = getDrillDownRootGroupBy(groupBy, subfilter);
  if (!subfilter?.length) {
    return [{ key: 'root', label: getGroupByLabel(rootGroupBy), targetDepth: 0 }];
  }

  const crumbs = [{ key: 'root', label: getGroupByLabel(rootGroupBy), targetDepth: 0 }];
  subfilter.forEach((f, i) => {
    const targetDepth = i + 1;
    crumbs.push({
      key: `subfilter-${i}`,
      label: formatSubfilterDisplay(f.groupBy, f.value),
      targetDepth: targetDepth < subfilter.length ? targetDepth : null,
    });
  });

  return crumbs;
}

function sanitizeExportFileNamePart(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getNegotiatorExportFileName({ stage, groupBy, selectedYear, drillDownBreadcrumbs }) {
  const parts = [
    sanitizeExportFileNamePart(groupBy ?? DEFAULT_GROUP_BY),
    sanitizeExportFileNamePart(stage ?? DEFAULT_STAGE),
    sanitizeExportFileNamePart(selectedYear ?? 'unknown'),
    ...(drillDownBreadcrumbs ?? [])
      .slice(1)
      .map((crumb) => sanitizeExportFileNamePart(crumb.label))
      .filter(Boolean),
    '_analysis',
  ];

  return parts.join('_');
}

const HEADER_NAMES = {
  ParcelID: 'Parcel ID',
  ParcelPrefix: 'Parcel prefix',
  Total: 'Total',
  offers_sent: 'Offers Sent',
  StartingAssessment: 'Previous Assessment',
  InitialMarketValue: 'Workup Value',
  SCARFiled: 'Filed',
  SCARIndexNumber: 'SCAR Index',
  SCARDeterminationAction: 'Determination Action',
  SCARDeterminationValue: 'Determination Value',
  SCARDeterminationDate: 'Determination Date',
  SCARSettleDate: 'Settle Date',
  SCARHearingOfficer: 'Hearing Officer',
  Negotiator: 'Negotiator',
  BARFiled: 'Filed',
  BARDeterminationAction: 'Determination Action',
  BARDeterminationValue: 'Determination Value',
  BARDeterminationDate: 'Determination Date',
  BARSettleDate: 'Settle Date',
  ART7Filed: 'Filed',
  ART7IndexNumber: 'Art 7 Index',
  ART7DeterminationAction: 'Determination Action',
  ART7DeterminationValue: 'Determination Value',
  ART7DeterminationDate: 'Determination Date',
  ART7SettleDate: 'Settle Date',
  ART7HearingOfficer: 'Hearing Officer',
  ART7HearingDate: 'Hearing Date',
  DecisionWin: 'Decision Win',
  DecisionLoss: 'Decision Loss',
  SettlementWin: 'Settlement Win',
  Win: 'Win',
  Loss: 'Loss',
  Unresolved: 'Unresolved',
  AwaitingDecision: 'Awaiting Decision',
  Resolved: 'Resolved',
  PriorIFMVTotal: 'Prior IFMV Total',
  Taxes: 'Taxes',
  TotalTaxes: 'Taxes Total',
  AvgTaxes: 'Avg Taxes',
  DeterminationValueTotal: 'Determination Value Total',
  AvgReduction: 'Avg Reduction',
  PortfolioReduction: 'Portfolio Reduction',
  SettlementAvgReduction: 'Settlement Avg Reduction',
  DecisionAvgReduction: 'Decision Avg Reduction',
  SettlementPortfolioReduction: 'Settlement Portfolio Reduction',
  DecisionPortfolioReduction: 'Decision Portfolio Reduction',
  Rev: 'Invoiced Rev',
  EstRev: 'Est. Rev',
  TotalRev: 'Total Rev',
  RevPerCase: '$ / Case',
  RevPerAvgTax: '($ / Case) / Avg Tax',
  ResolvedPct: 'Resolved %',
  WinRate: 'Win Rate',
  AssessmentRatio: 'Ratio',
  EQPrior: 'EQ Prior',
  EQDet: 'EQ Det',
  Reduction: '% Reduction',
  CountyCode: 'County',
  AlgoDisagree: 'Algo disagree',
  AvgTwoLowOptimize: 'Avg 2-lowest opt. idx',
  SelectedIndexOptimize: 'Optimized indexes',
};

const DISPLAY_ORDER = [
  'ParcelID',
  'CountyCode',
  'Filed',
  'SCARIndexNumber',
  'ART7IndexNumber',
  'Result',
  'DeterminationAction',
  'DeterminationValue',
  'StartingAssessment',
  'InitialMarketValue',
  'AssessmentRatio',
  'EQPrior',
  'EQDet',
  'Reduction',
  'Taxes',
  'Total',
  'offers_sent',
  'Resolved',
  'Win',
  'Loss',
  'AlgoDisagree',
  'AvgTwoLowOptimize',
  'SelectedIndexOptimize',
  'AwaitingDecision',
  'SettlementWin',
  'DecisionWin',
  'DecisionLoss',
  'Unresolved',
  'DeterminationDate',
  'SettleDate',
  'HearingDate',
  'HearingOfficer',
  'Negotiator',
  'PriorIFMVTotal',
  'TotalTaxes',
  'AvgTaxes',
  'DeterminationValueTotal',
  'AvgReduction',
  'SettlementAvgReduction',
  'DecisionAvgReduction',
  'PortfolioReduction',
  'SettlementPortfolioReduction',
  'DecisionPortfolioReduction',
  'Rev',
  'EstRev',
  'TotalRev',
  'RevPerCase',
  'RevPerAvgTax',
];

const FIXED_FIELDS = ['ParcelID', 'SCARIndexNumber', 'ART7IndexNumber', 'StartingAssessment', 'InitialMarketValue', 'AssessmentRatio', 'EQPrior', 'EQDet', 'Reduction', 'Result', 'Taxes', 'Rev', 'EstRev', 'Negotiator', 'Win', 'Loss', 'AlgoDisagree', 'SelectedIndexOptimize', 'SettlementWin', 'DecisionWin', 'DecisionLoss', 'Unresolved', 'Resolved', 'AwaitingDecision', 'offers_sent'];
const DATE_FIELD_SUFFIXES = ['DeterminationDate', 'SettleDate', 'HearingDate'];

/** Shown in the grid (and filter panel) but omitted from the Columns toolbar checklist. */
const COLUMNS_PANEL_EXCLUDED_FIELDS = ['CountyCode'];

/** Hidden by default in individual (per-parcel) rows only; aggregate view keeps these visible unless the user hides them. */
const INDIVIDUAL_ONLY_HIDDEN_COLUMNS = [
  'SCARFiled',
  'BARFiled',
  'ART7Filed',
  'SCARDeterminationAction',
  'BARDeterminationAction',
  'ART7DeterminationAction',
  'SelectedIndexOptimize',
  'AwaitingDecision',
];

const DEFAULT_HIDDEN_COLUMNS = [
  'SettlementAvgReduction',
  'DecisionAvgReduction',
  'SettlementPortfolioReduction',
  'DecisionPortfolioReduction',
  'PriorIFMVTotal',
  'TotalTaxes',
  'AvgTaxes',
  'RevPerAvgTax',
  'DeterminationValueTotal',
  'Loss',
  'SettlementWin',
  'DecisionWin',
  'DecisionLoss',
  'Unresolved',
  'AlgoDisagree',
  'AvgTwoLowOptimize',
  'CountyCode',
];

const DEFAULT_COLUMN_VISIBILITY_MODEL = DEFAULT_HIDDEN_COLUMNS.reduce(
  (acc, field) => ({ ...acc, [field]: false }),
  {},
);

function getDefaultNegotiatorAnalysisYear() {
  const now = new Date();
  const y = now.getFullYear();
  return now.getMonth() < 6 ? y - 1 : y;
}

function formatDateMmDdYy(value) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

function compareDateValues(v1, v2) {
  if (v1 == null && v2 == null) return 0;
  if (v1 == null) return -1;
  if (v2 == null) return 1;
  const d1 = v1 instanceof Date ? v1 : new Date(v1);
  const d2 = v2 instanceof Date ? v2 : new Date(v2);
  if (Number.isNaN(d1.getTime()) && Number.isNaN(d2.getTime())) return 0;
  if (Number.isNaN(d1.getTime())) return -1;
  if (Number.isNaN(d2.getTime())) return 1;
  return d1.getTime() - d2.getTime();
}

function getOrderedFieldNames(stage, availableKeys) {
  if (!Array.isArray(availableKeys) || availableKeys.length === 0) return ['ParcelID'];
  const prefix = stage || 'SCAR';
  const ordered = [];
  for (const key of DISPLAY_ORDER) {
    if (key === 'CountyCode') {
      if (availableKeys.includes('CountyCode')) ordered.push('CountyCode');
      continue;
    }
    const field = FIXED_FIELDS.includes(key) ? key : prefix + key;
    if (availableKeys.includes(field)) ordered.push(field);
  }
  return ordered.length ? ordered : ['ParcelID'];
}

function getAggregateOrderedFieldNames(availableKeys) {
  if (!Array.isArray(availableKeys) || availableKeys.length === 0) return ['ParcelPrefix'];
  const preferredOrder = ['ParcelPrefix', 'CountyCode', ...DISPLAY_ORDER.filter((k) => k !== 'ParcelID' && k !== 'CountyCode')];
  const ordered = [];
  for (const field of preferredOrder) {
    if (availableKeys.includes(field)) ordered.push(field);
  }
  return ordered.length ? ordered : ['ParcelPrefix'];
}

const COUNT_FIELDS = ['Total', 'offers_sent', 'DecisionWin', 'DecisionLoss', 'SettlementWin', 'Win', 'Loss', 'AlgoDisagree', 'Unresolved', 'AwaitingDecision', 'Resolved'];

const CURRENCY_AGG_FIELDS = ['PriorIFMVTotal', 'TotalTaxes', 'AvgTaxes', 'DeterminationValueTotal', 'Rev', 'EstRev', 'TotalRev', 'RevPerCase'];
const PERCENT_AGG_FIELDS = ['AvgReduction', 'PortfolioReduction', 'SettlementAvgReduction', 'DecisionAvgReduction', 'SettlementPortfolioReduction', 'DecisionPortfolioReduction', 'RevPerAvgTax'];
const DECIMAL_INDEX_AGG_FIELDS = ['AvgTwoLowOptimize'];

/** Strip ART7/BAR/SCAR prefix for tooltip lookup on individual-view columns. */
function negotiatorFieldSuffix(field) {
  if (field.startsWith('ART7')) return field.slice(4);
  if (field.startsWith('SCAR')) return field.slice(4);
  if (field.startsWith('BAR')) return field.slice(3);
  return field;
}

/** Grouped-row metrics: how each value is computed on the server. */
const AGGREGATE_HEADER_TOOLTIPS = {
  Total: 'Count of filed cases in this group (rows where Filed is set). Other metrics use this row set unless noted.',
  offers_sent: 'Count of live Aventine offers in this group for the selected tax year (OfferedBy Aventine, LiveOffer=1, Negotiations.Year). Uses the same date filter bounds when set. County and drill-down filters apply. Unassigned negotiator offers are included in the Unassigned row.',
  Resolved: 'Wins plus losses in the group (excludes unresolved and awaiting-decision rows). Shown as count with resolved % of filed cases.',
  PriorIFMVTotal: 'Sum of EQ Prior across resolved cases in the group.',
  DeterminationValueTotal: 'Sum of EQ Det across resolved cases in the group.',
  TotalTaxes: 'Sum of Taxes (COALESCE(TaxesTotal, EstimatedTaxes) from Tax) across resolved cases only—not all filed cases.',
  AvgTaxes: 'Average Taxes per filed case in the group: sum of Taxes for every filed row ÷ Total filed count (includes unresolved).',
  AvgReduction: 'Mean of ((Determination value − Starting assessment) ÷ Starting assessment) over wins where prior assessment > 0.',
  PortfolioReduction: 'Group-level: (sum of EQ Det − sum of EQ Prior) ÷ sum of EQ Prior for resolved cases.',
  SettlementAvgReduction: 'Same relative reduction as Avg Reduction, averaged only over settlement wins with prior > 0.',
  DecisionAvgReduction: 'Same relative reduction as Avg Reduction, averaged only over decision wins with prior > 0.',
  SettlementPortfolioReduction: 'Portfolio-style reduction using only cases counted as settlement path (settlement wins and settlement losses): (sum EQ Det − sum EQ Prior) ÷ sum EQ Prior.',
  DecisionPortfolioReduction: 'Portfolio-style reduction using only decision-path cases: (sum EQ Det − sum EQ Prior) ÷ sum EQ Prior.',
  TotalRev: 'Sum of Invoiced Rev plus Est. Rev for all filed rows in the group.',
  RevPerCase: 'Total Rev ÷ resolved cases in the group (wins + losses).',
  RevPerAvgTax: '($ / Case) ÷ Avg Taxes, shown as a percent. ($ / Case) is Total Rev ÷ resolved cases; Avg Taxes is the average tax bill per filed case (sum of Taxes ÷ filed count, including unresolved). Blank when the average tax bill is zero.',
  ResolvedPct: 'Resolved ÷ Total filed cases in the group.',
  Win: 'Count of wins in the group (decision wins + settlement wins). Shown as count with win rate among resolved cases.',
  Loss: 'Count of losses in the group.',
  AlgoDisagree: 'Count of filed cases with saved comps and non-empty optimized indexes where the outside-tolerance rule fires (missing/empty optimized indexes are excluded; cases whose top 3 saved comps include both optimize ranks 1 and 2 are excluded).',
  AvgTwoLowOptimize: 'Mean over filed cases in the group of (average of the two smallest numeric SelectedIndexOptimize entries per case); one entry uses that value; cases with no numeric indexes omitted from the mean.',
  DecisionWin: 'Wins attributed to a hearing decision (SCAR: hearing officer set; BAR/Art 7: win without settle date).',
  SettlementWin: 'Wins attributed to settlement (SCAR: no hearing officer; BAR/Art 7: win with settle date).',
  DecisionLoss: 'Losses attributed to a hearing decision.',
  Unresolved: 'Rows that are not a win or loss (includes awaiting decision).',
  AwaitingDecision: 'Rows whose action is Awaiting Decision (D).',
  Rev: 'Sum of Invoiced Rev for filed rows in the group (per-parcel invoicing rules on wins).',
  EstRev: 'Sum of estimated Rev for filed rows when invoiced Rev is not used.',
};

/** Per-parcel columns derived on the server (individual view). */
const INDIVIDUAL_ROW_TOOLTIPS = {
  AssessmentRatio: 'Previous assessment ÷ IFMV Town when IFMV Town > 0; used to equalize values.',
  EQPrior: 'Equalized prior: previous assessment ÷ ratio when ratio is set; otherwise previous assessment.',
  EQDet: 'Equalized determination: determination value ÷ ratio when ratio is set; otherwise raw determination value.',
  Reduction: '(Determination value − Previous assessment) ÷ Previous assessment. Same per-case reduction averaged into Avg Reduction for wins. Blank when previous assessment is missing or zero, or determination value is missing.',
  Rev: 'Invoiced revenue on wins when TaxSavings and fee apply (tax savings × fee plus filing and appraisal fees), else blank. Blank for NM wins (no revenue).',
  EstRev: 'Estimated revenue on wins using retainer percent and tax-based formula when invoiced Rev is not used. Blank for NM wins (no revenue).',
  DecisionWin: 'Win counted as a hearing decision (SCAR: hearing officer present; BAR/Art 7: win without settle date).',
  SettlementWin: 'Win counted as settlement (SCAR: no hearing officer; BAR/Art 7: win with settle date).',
  DecisionLoss: 'Loss with a hearing decision (SCAR: hearing officer; BAR/Art 7: not settled).',
  Win: '1 if decision win or settlement win for this parcel.',
  Loss: '1 if action is a loss code for this stage.',
  Unresolved: '1 if neither win nor loss (includes awaiting decision).',
  AwaitingDecision: '1 if action is Awaiting Decision (D).',
  Result: 'Resolved outcome: Won (Decision), Won (Settled), Lost (Decision), or Lost (Settled); derived from win/loss flags for this stage.',
  DeterminationAction: 'Determination action code recorded for this stage (e.g. S, ST, NM, SD, W, BS, BT, BD, D).',
  Resolved: '1 if win or loss (same row is counted in portfolio reductions).',
  AlgoDisagree: '1 if there are saved comps, SelectedIndexOptimize is non-empty with at least one index, and the outside-tolerance rule applies; 0 if optimized data is missing/empty, the top 3 saved comps include both optimize ranks 1 and 2, or the rule does not apply.',
  SelectedIndexOptimize: 'Per-comp optimized rank indexes from ComparablesWK (Unequal), aligned to saved comp order; empty when none.',
  Taxes: 'COALESCE(TaxesTotal, EstimatedTaxes) from the Tax table for this parcel and year.',
  IndexNumber: 'Court index number for this parcel and year.',
  offers_sent: 'Count of live Aventine offers for this parcel and selected tax year (OfferedBy Aventine, LiveOffer=1, Negotiations.Year). Uses the same date filter bounds when set.',
};

function getNegotiatorColumnDescriptionBody(field, isAggregate) {
  if (field === 'CountyCode') {
    return 'First character of Parcel ID (county code). For county or municipality groups, derived from the group code. Empty when a negotiator or hearing-officer group mixes multiple counties. Hidden by default; use Filters to filter by county. This column is not listed under the Columns button.';
  }
  if (isAggregate) return AGGREGATE_HEADER_TOOLTIPS[field] ?? null;
  return INDIVIDUAL_ROW_TOOLTIPS[negotiatorFieldSuffix(field)] ?? null;
}

function formatNumber(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? value : n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatSelectedIndexOptimize(val) {
  if (val == null || val === '') return '';
  if (Array.isArray(val)) {
    return val.map((x) => (x == null ? '-' : String(x))).join(', ');
  }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? formatSelectedIndexOptimize(parsed) : val;
    } catch {
      return val;
    }
  }
  return String(val);
}

function formatPercent(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? value : `${(n * 100).toFixed(1)}%`;
}

function individualReductionFraction(row, stage) {
  const prior = Number(row?.StartingAssessment);
  if (!(prior > 0)) return null;
  const det = row?.[`${stage || 'SCAR'}DeterminationValue`];
  if (det == null || det === '') return null;
  const detNum = Number(det);
  return Number.isNaN(detNum) ? null : (detNum - prior) / prior;
}

function formatCountWithPercent(count, rateFraction) {
  const { countStr, percentStr } = getCountWithPercentDisplay(count, rateFraction);
  return percentStr ? `${countStr} (${percentStr})` : countStr;
}

function getCountWithPercentDisplay(count, rateFraction) {
  const countStr = formatNumber(count);
  if (rateFraction == null || rateFraction === '') {
    return { countStr, percentStr: null };
  }
  const rate = Number(rateFraction);
  if (Number.isNaN(rate)) {
    return { countStr, percentStr: null };
  }
  return { countStr, percentStr: `${Math.round(rate * 100)}%` };
}

function renderCountWithPercentCell(rateField) {
  return (params) => {
    const { countStr, percentStr } = getCountWithPercentDisplay(params.value, params.row?.[rateField]);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          width: '100%',
          height: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        <Box component="span" sx={{ lineHeight: 1.2 }}>{countStr}</Box>
        {percentStr && (
          <Typography
            component="span"
            variant="caption"
            sx={{ color: 'text.secondary', lineHeight: 1.2 }}
          >
            ({percentStr})
          </Typography>
        )}
      </Box>
    );
  };
}

function safeNum(val) {
  if (val == null || val === '') return 0;
  const n = Number(val);
  return Number.isNaN(n) ? 0 : n;
}

function gridFilterIsActive(filterModel) {
  const items = filterModel?.items || [];
  const quick = filterModel?.quickFilterValues || [];
  return items.some((item) => item?.field) || quick.some((v) => String(v ?? '').trim() !== '');
}

const EMPTY_GRID_FILTER_MODEL = {
  items: [],
  logicOperator: GridLogicOperator.And,
  quickFilterValues: [],
};

const DEFAULT_COLUMN_TYPES = getGridDefaultColumnTypes();

function isGridFilterItemValid(item, column) {
  if (!item?.field || !item?.operator || !column || column.filterable === false) return false;
  const operators = column.filterOperators
    ?? (DEFAULT_COLUMN_TYPES[column.type] ?? DEFAULT_COLUMN_TYPES.string).filterOperators;
  return operators.some((op) => op.value === item.operator);
}

function getSafeGridFilterModel(filterModel, columns) {
  const columnByField = new Map(columns.map((col) => [col.field, col]));
  return {
    ...filterModel,
    items: (filterModel?.items || []).filter((item) => {
      const column = columnByField.get(item?.field);
      return column && isGridFilterItemValid(item, column);
    }),
    quickFilterValues: filterModel?.quickFilterValues || [],
    logicOperator: filterModel?.logicOperator ?? GridLogicOperator.And,
  };
}

function getFilteredRowsAll(api) {
  try {
    const allIds = typeof api.getAllRowIds === 'function'
      ? api.getAllRowIds()
      : typeof api.getSortedRowIds === 'function'
        ? api.getSortedRowIds()
        : [];
    const lookup = api.state?.filter?.filteredRowsLookup || {};
    return allIds
      .filter((id) => lookup[id] !== false)
      .map((id) => api.getRow(id))
      .filter((row) => row && row.id !== 'totals');
  } catch {
    return [];
  }
}

function computeAggregateTotalsFromRows(filteredRows) {
  const totals = {
    Total: 0,
    offers_sent: 0,
    Win: 0,
    DecisionWin: 0,
    SettlementWin: 0,
    Loss: 0,
    DecisionLoss: 0,
    AwaitingDecision: 0,
    Unresolved: 0,
    Resolved: 0,
    AlgoDisagree: 0,
    PriorIFMVTotal: 0,
    TotalTaxes: 0,
    taxes_filed_sum: 0,
    DeterminationValueTotal: 0,
    reduction_sum: 0,
    reduction_count: 0,
    settlement_reduction_sum: 0,
    settlement_reduction_count: 0,
    settlement_prior_total: 0,
    settlement_det_total: 0,
    decision_reduction_sum: 0,
    decision_reduction_count: 0,
    decision_prior_total: 0,
    decision_det_total: 0,
    Rev: 0,
    EstRev: 0,
    OptimizeTwoLowAvg_sum: 0,
    OptimizeTwoLowAvg_n: 0,
  };

  for (const row of filteredRows) {
    totals.Total += safeNum(row.Total);
    totals.offers_sent += safeNum(row.offers_sent);
    totals.Win += safeNum(row.Win);
    totals.DecisionWin += safeNum(row.DecisionWin);
    totals.SettlementWin += safeNum(row.SettlementWin);
    totals.Loss += safeNum(row.Loss);
    totals.DecisionLoss += safeNum(row.DecisionLoss);
    totals.AwaitingDecision += safeNum(row.AwaitingDecision);
    totals.Unresolved += safeNum(row.Unresolved);
    totals.Resolved += safeNum(row.Resolved);
    totals.AlgoDisagree += safeNum(row.AlgoDisagree);
    totals.Rev += safeNum(row.Rev);
    totals.EstRev += safeNum(row.EstRev);
    totals.PriorIFMVTotal += safeNum(row.PriorIFMVTotal);
    totals.TotalTaxes += safeNum(row.TotalTaxes);
    totals.DeterminationValueTotal += safeNum(row.DeterminationValueTotal);
    totals.taxes_filed_sum += safeNum(row.AvgTaxes) * safeNum(row.Total);

    const win = safeNum(row.Win);
    if (row.AvgReduction != null && win > 0) {
      totals.reduction_sum += safeNum(row.AvgReduction) * win;
      totals.reduction_count += win;
    }

    const settlementWin = safeNum(row.SettlementWin);
    if (row.SettlementAvgReduction != null && settlementWin > 0) {
      totals.settlement_reduction_sum += safeNum(row.SettlementAvgReduction) * settlementWin;
      totals.settlement_reduction_count += settlementWin;
    }

    const decisionWin = safeNum(row.DecisionWin);
    if (row.DecisionAvgReduction != null && decisionWin > 0) {
      totals.decision_reduction_sum += safeNum(row.DecisionAvgReduction) * decisionWin;
      totals.decision_reduction_count += decisionWin;
    }

    const resolved = safeNum(row.Resolved);
    if (resolved > 0) {
      const settlementCases = settlementWin + Math.max(0, safeNum(row.Loss) - safeNum(row.DecisionLoss));
      const decisionCases = decisionWin + safeNum(row.DecisionLoss);
      if (settlementCases > 0) {
        totals.settlement_prior_total += (safeNum(row.PriorIFMVTotal) * settlementCases) / resolved;
        totals.settlement_det_total += (safeNum(row.DeterminationValueTotal) * settlementCases) / resolved;
      }
      if (decisionCases > 0) {
        totals.decision_prior_total += (safeNum(row.PriorIFMVTotal) * decisionCases) / resolved;
        totals.decision_det_total += (safeNum(row.DeterminationValueTotal) * decisionCases) / resolved;
      }
    }

    if (row.AvgTwoLowOptimize != null) {
      const filedCount = safeNum(row.Total);
      if (filedCount > 0) {
        totals.OptimizeTwoLowAvg_sum += safeNum(row.AvgTwoLowOptimize) * filedCount;
        totals.OptimizeTwoLowAvg_n += filedCount;
      }
    }
  }

  return totals;
}

function finalizeAggregateTotals(totals) {
  const totalRevSum = safeNum(totals.Rev) + safeNum(totals.EstRev);
  const revPerCase = totals.Resolved > 0 ? totalRevSum / totals.Resolved : 0;
  const avgTaxes = totals.Total > 0 ? totals.taxes_filed_sum / totals.Total : 0;
  return {
    ParcelPrefix: 'Total',
    CountyCode: '',
    id: 'totals',
    Total: totals.Total,
    offers_sent: totals.offers_sent,
    Win: totals.Win,
    DecisionWin: totals.DecisionWin,
    SettlementWin: totals.SettlementWin,
    Loss: totals.Loss,
    DecisionLoss: totals.DecisionLoss,
    AwaitingDecision: totals.AwaitingDecision,
    Unresolved: totals.Unresolved,
    Resolved: totals.Resolved,
    AlgoDisagree: totals.AlgoDisagree,
    PriorIFMVTotal: totals.PriorIFMVTotal,
    TotalTaxes: totals.TotalTaxes,
    DeterminationValueTotal: totals.DeterminationValueTotal,
    Rev: totals.Rev,
    EstRev: totals.EstRev,
    TotalRev: totalRevSum,
    RevPerCase: revPerCase,
    RevPerAvgTax: avgTaxes > 0 ? revPerCase / avgTaxes : null,
    ResolvedPct: totals.Total > 0 ? totals.Resolved / totals.Total : 0,
    WinRate: totals.Resolved > 0 ? totals.Win / totals.Resolved : 0,
    AvgReduction: totals.reduction_count > 0 ? totals.reduction_sum / totals.reduction_count : 0,
    AvgTaxes: avgTaxes,
    PortfolioReduction: totals.PriorIFMVTotal > 0
      ? (totals.DeterminationValueTotal - totals.PriorIFMVTotal) / totals.PriorIFMVTotal
      : 0,
    SettlementAvgReduction: totals.settlement_reduction_count > 0
      ? totals.settlement_reduction_sum / totals.settlement_reduction_count
      : null,
    DecisionAvgReduction: totals.decision_reduction_count > 0
      ? totals.decision_reduction_sum / totals.decision_reduction_count
      : null,
    SettlementPortfolioReduction: totals.settlement_prior_total > 0
      ? (totals.settlement_det_total - totals.settlement_prior_total) / totals.settlement_prior_total
      : null,
    DecisionPortfolioReduction: totals.decision_prior_total > 0
      ? (totals.decision_det_total - totals.decision_prior_total) / totals.decision_prior_total
      : null,
    AvgTwoLowOptimize: totals.OptimizeTwoLowAvg_n > 0
      ? totals.OptimizeTwoLowAvg_sum / totals.OptimizeTwoLowAvg_n
      : null,
  };
}

function buildAggregatePinnedRows(results) {
  if (!results?.totals) return undefined;
  return { bottom: [{ ...results.totals, CountyCode: '', id: 'totals' }] };
}

function formatIndividualResult(row) {
  if (!row) return '';
  if (row.DecisionWin) return 'Won (Decision)';
  if (row.SettlementWin) return 'Won (Settled)';
  if (row.DecisionLoss) return 'Lost (Decision)';
  if (row.Loss) return 'Lost (Settled)';
  if (row.AwaitingDecision) return 'Awaiting Decision';
  return '';
}

// Inner function to determine what fields are displayed based on what view we are in.
function buildColumns(fieldKeys, isAggregate = false, groupBy = null) {
  if (!Array.isArray(fieldKeys) || fieldKeys.length === 0) return [{ field: 'ParcelID', headerName: 'Parcel ID', flex: 1, minWidth: 140 }];
  return fieldKeys.map((field) => {
    const isDate = DATE_FIELD_SUFFIXES.some((s) => field.endsWith(s));
    const isOffersSent = field === 'offers_sent';
    const isCountField = COUNT_FIELDS.includes(field) && !isOffersSent;
    const isGroupingColumn = isAggregate && field === 'ParcelPrefix';
    const isAggNumber = isAggregate && CURRENCY_AGG_FIELDS.includes(field);
    const isPercent = (isAggregate && PERCENT_AGG_FIELDS.includes(field)) || (!isAggregate && field === 'Reduction');
    const isDecimalIndexAgg = isAggregate && DECIMAL_INDEX_AGG_FIELDS.includes(field);
    const headerName = isGroupingColumn && groupBy ? (GROUP_BY_HEADER_NAMES[groupBy] ?? 'Parcel prefix') : (HEADER_NAMES[field] ?? field);
    const isNegotiator = field === 'Negotiator';
    const isNegotiatorGroupColumn = isGroupingColumn && groupBy === 'negotiator';
    const isHearingDateGroupColumn = isGroupingColumn && groupBy === 'hearing_date';
    const isParcelId = field === 'ParcelID';
    const isScarIndexNumber = field === 'SCARIndexNumber' || field === 'ART7IndexNumber';
    const isCountyCode = field === 'CountyCode';
    const isSelectedIndexOptimize = field === 'SelectedIndexOptimize';
    const isResolvedAggregate = isAggregate && field === 'Resolved';
    const isWinAggregate = isAggregate && field === 'Win';
    const isRevMetricAggregate = isAggregate && (field === 'TotalRev' || field === 'RevPerCase' || field === 'RevPerAvgTax');
    const isIndividualNumberField = !isAggregate && (
      field === 'StartingAssessment'
      || field === 'Taxes'
      || field === 'EQPrior'
      || field === 'EQDet'
      || field === 'InitialMarketValue'
      || field.endsWith('DeterminationValue')
      || isOffersSent
    );
    const descriptionBody = isGroupingColumn ? null : getNegotiatorColumnDescriptionBody(field, isAggregate);
    return {
      field,
      headerName,
      flex: 1,
      minWidth: field === 'ParcelID' || field === 'ParcelPrefix' ? 140 : field === 'RevPerAvgTax' ? 170 : isSelectedIndexOptimize ? 150 : 110,
      ...(descriptionBody && {
        description: `${headerName}\n${descriptionBody}`,
      }),
      ...(isParcelId && { renderCell: (params) => <CopyableIdCell value={params.value ?? params.row?.ParcelID} label="Parcel ID" /> }),
      ...(isScarIndexNumber && {
        sortComparator: scarIndexSortComparator,
        renderCell: (params) => <CopyableIdCell value={params.value ?? params.row?.[field]} label={headerName} />,
      }),
      ...(isCountyCode && {
        type: 'singleSelect',
        filterable: false,
        sortable: false,
        valueOptions: COUNTY_SINGLE_SELECT_OPTIONS,
        valueFormatter: (value) => (value ? (COUNTY_CODES[value] ?? value) : ''),
      }),
      ...(isDate && {
        sortComparator: compareDateValues,
        valueFormatter: (value) => formatDateMmDdYy(value),
      }),
      ...(isNegotiator && { valueFormatter: (value) => getUserNameById(value) ?? '-' }),
      ...(isNegotiatorGroupColumn && { valueFormatter: (value, row) => (row?.id === 'totals' ? value : getUserNameById(value) ?? value) }),
      ...(isHearingDateGroupColumn && {
        valueFormatter: (value, row) => (row?.id === 'totals' ? value : formatDateMmDdYy(value) || value),
        sortComparator: (v1, v2) => {
          if (v1 === 'Total') return 1;
          if (v2 === 'Total') return -1;
          return compareDateValues(v1, v2);
        },
      }),
      ...(isOffersSent && { type: 'number', filterable: true }),
      ...(isCountField && (isAggregate ? { type: 'number', filterable: true } : { type: 'boolean', filterable: true })),
      ...(isGroupingColumn && { cellClassName: (params) => (params.id === 'totals' ? '' : 'clickable-cell') }),
      ...(isAggNumber && { type: 'number', valueFormatter: (value) => formatNumber(value) }),
      ...(isDecimalIndexAgg && {
        type: 'number',
        valueFormatter: (value) =>
          value != null && value !== '' && !Number.isNaN(Number(value)) ? Number(value).toFixed(2) : '',
      }),
      ...(isPercent && {
        type: 'number',
        valueFormatter: (value) => formatPercent(value),
      }),
      ...(isResolvedAggregate && {
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value, row) => formatCountWithPercent(value, row?.ResolvedPct),
        renderCell: renderCountWithPercentCell('ResolvedPct'),
      }),
      ...(isWinAggregate && {
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value, row) => formatCountWithPercent(value, row?.WinRate),
        renderCell: renderCountWithPercentCell('WinRate'),
      }),
      ...(isRevMetricAggregate && { cellClassName: 'rev-metric-cell' }),
      ...(!isAggregate && field === 'AssessmentRatio' && { valueFormatter: (v) => (v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : v ?? '') }),
      ...(isIndividualNumberField && { valueFormatter: (v) => formatNumber(v) }),
      ...(!isAggregate && isSelectedIndexOptimize && {
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) => formatSelectedIndexOptimize(row?.SelectedIndexOptimize),
      }),
      ...(field === 'Result' && {
        cellClassName: (params) => {
          if (params.row?.DecisionWin || params.row?.SettlementWin) return 'result-won-cell';
          if (params.row?.DecisionLoss || params.row?.Loss) return 'result-lost-cell';
          return '';
        },
      }),
    };
  });
}

/** First parcel-ID character = county code; for county/muni aggregate rows, group_code carries that prefix. */
function deriveCountyCode(row, isAggregate, groupByFromApi) {
  const pid = row.ParcelID;
  if (pid != null && String(pid).trim() !== '') {
    return String(pid).slice(0, 1);
  }
  if (!isAggregate) return '';
  const gc = row.group_code;
  if (gc == null || gc === '') return '';
  const s = String(gc);
  if (groupByFromApi === 'county' || groupByFromApi === 'municipality') {
    return s.slice(0, 1);
  }
  return '';
}

function buildNegotiatorGridModel(results, stage, groupBy) {
  const groupByFromApi = results?.group_by ?? groupBy;
  const data = results?.rows;
  const isAggregate = Array.isArray(data) && data.length > 0 && data[0] && 'ParcelPrefix' in data[0];
  const stagePrefix = results?.stage ?? stage;

  let rows;

  // This is sort of an unintended bug. Theoretically it should NOT be returning multiple rows but it does
  // for example if you were to have a voided invoice (D13 - 2025)
  if (Array.isArray(data) && data.length > 0) {
    // DataGrid row id must be unique; API can return the same ParcelID twice (e.g. multiple courtfile rows).
    rows = data.map((r, i) => {
      const base = r.ParcelPrefix ?? r.ParcelID ?? 'row';
      const CountyCode = deriveCountyCode(r, isAggregate, groupByFromApi);
      return {
        ...r,
        CountyCode,
        ...(!isAggregate && { Reduction: individualReductionFraction(r, stagePrefix), Result: formatIndividualResult(r) }),
        id: `${base}-${i}`,
      };
    });
  } else {
    const ids = results?.parcel_ids;
    rows = Array.isArray(ids)
      ? ids.map((pid, i) => {
        const ParcelID = pid;
        const CountyCode = deriveCountyCode({ ParcelID }, false, groupByFromApi);
        return { id: `${pid ?? 'row'}-${i}`, ParcelID, CountyCode };
      })
      : [];
  }

  const pinnedRows = isAggregate ? buildAggregatePinnedRows(results) : undefined;

  const keys = Array.isArray(data) && data[0] ? Object.keys(data[0]) : ['ParcelID'];
  const includeIndividualDerived = !isAggregate && Array.isArray(data) && data.length > 0;
  const keysWithCounty = [...new Set([...keys, 'CountyCode', ...(includeIndividualDerived ? ['Reduction', 'Result'] : [])])];
  const orderedKeys = isAggregate
    ? getAggregateOrderedFieldNames(keysWithCounty)
    : getOrderedFieldNames(results?.stage ?? stage, keysWithCounty);

  const columns = buildColumns(orderedKeys, isAggregate, groupByFromApi);

  return { groupByFromApi, isAggregate, rows, pinnedRows, columns };
}

const NEGOTIATOR_GRID_SLOTS = {
  toolbar: NegotiatorToolbar,
  filterPanel: NegotiatorAnalysisCustomFiltersPanel,
};

function NegotiatorToolbar({
  stage,
  onStageChange,
  groupBy,
  onGroupByChange,
  drillDownBreadcrumbs,
  onNavigateDrillDown,
  onClearCustomFilters,
  showClearCustomFilters,
  yearOptions,
  selectedYear,
  onYearChange,
}) {
  const exportFileName = getNegotiatorExportFileName({
    stage,
    groupBy,
    selectedYear,
    drillDownBreadcrumbs,
  });

  return (
    <Toolbar sx={{ p: 0, alignItems: 'center', bgcolor: 'white', borderBottom: 1, borderColor: '#e0e0e0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, flexWrap: 'wrap', width: '100%' }}>
        <Box sx={toolbarIconGroupSx}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExportButton options={{ fileName: exportFileName, delimiter: ',' }} />
          <CollapsibleQuickFilter debounceMs={200} />
          {showClearCustomFilters && (
            <Typography
              component="button"
              type="button"
              onClick={onClearCustomFilters}
              sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', border: 0, background: 'none', px: 0, whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Breadcrumbs
            aria-label="negotiator analysis navigation"
            sx={{
              '& .MuiBreadcrumbs-li': { maxWidth: 200 },
              '& .MuiBreadcrumbs-li > *': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              },
            }}
          >
            {drillDownBreadcrumbs.map((crumb, i) => {
              const isLast = i === drillDownBreadcrumbs.length - 1;
              if (isLast || crumb.targetDepth == null) {
                return (
                  <Typography key={crumb.key} color="text.primary" variant="body2" fontWeight={isLast ? 600 : 400}>
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Link
                  key={crumb.key}
                  component="button"
                  type="button"
                  underline="hover"
                  color="primary"
                  variant="body2"
                  onClick={() => onNavigateDrillDown(crumb.targetDepth)}
                  sx={{ border: 0, background: 'none', cursor: 'pointer', p: 0, textAlign: 'left' }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <YearTabsSelector
            activeYear={String(selectedYear)}
            availableYears={yearOptions.map(String)}
            onChange={(_e, y) => onYearChange(Number(y))}
            sx={{ maxWidth: 320 }}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Group by</InputLabel>
          <Select
            value={groupBy ?? DEFAULT_GROUP_BY}
            label="Group by"
            onChange={(e) => onGroupByChange(e.target.value)}
          >
            {GROUP_BY_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={(opt.value === 'hearing_officer' || opt.value === 'hearing_date') && !STAGE_HAS_HEARING.has(stage)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Stage</InputLabel>
          <Select
            value={stage ?? DEFAULT_STAGE}
            label="Stage"
            onChange={(e) => onStageChange(e.target.value)}
          >
            {STAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Toolbar>
  );
}

function NegotiatorAnalysisRebuilt({ userId, stage: stageProp }) {
  const apiRef = useGridApiRef();
  const [stage, setStage] = useState(() => stageProp ?? DEFAULT_STAGE);
  const [groupBy, setGroupBy] = useState(DEFAULT_GROUP_BY);
  const [subfilter, setSubfilter] = useState(null);
  const [analysisCustomFilters, setAnalysisCustomFilters] = useState(() => [newNegotiatorAnalysisCustomFilterRow()]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(DEFAULT_COLUMN_VISIBILITY_MODEL);
  const [analysisYear, setAnalysisYear] = useState(getDefaultNegotiatorAnalysisYear);

  const yearOptions = useMemo(() => getTaxYearOptions().map((o) => o.value).reverse(), []);

  useEffect(() => {
    if (!yearOptions.includes(analysisYear)) {
      setAnalysisYear(getDefaultNegotiatorAnalysisYear());
    }
  }, [yearOptions, analysisYear]);

  const fetchGenRef = useRef(0);

  const fetchNegotiatorResults = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const y = analysisYear;
      const scope = deriveNegotiatorAnalysisCustomQuery(analysisCustomFilters);
      const params = new URLSearchParams({ year: String(y), group_by: groupBy });
      if (userId) params.set('user_id', userId);
      if (stage) params.set('stage', stage);
      if (scope.startingDateFilter) params.set('startingDateFilter', scope.startingDateFilter);
      if (scope.endingDateFilter) params.set('endingDateFilter', scope.endingDateFilter);
      if (subfilter?.length) {
        subfilter.forEach((f) => {
          params.append('subfilter_group_by', f.groupBy);
          params.append('subfilter_value', String(f.value));
        });
      }
      if (scope.algorithmAgreement) params.set('algorithmAgreement', scope.algorithmAgreement);
      if (scope.trialDecision) params.set('trialDecision', '1');
      if (scope.townPortal) params.set('townPortal', '1');
      scope.countyCodes.forEach((c) => params.append('countyCode', c));
      const response = await axiosInstance.get(`/negotiator_results/all?${params}`);
      if (gen !== fetchGenRef.current) return;
      setResults(response.data);
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load results';
      setError(msg);
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
      }
    }
  }, [userId, analysisYear, stage, groupBy, subfilter, analysisCustomFilters]);

  useEffect(() => {
    fetchNegotiatorResults();
  }, [fetchNegotiatorResults]);

  useEffect(() => {
    if (!STAGE_HAS_HEARING.has(stage) && (groupBy === 'hearing_officer' || groupBy === 'hearing_date')) {
      setGroupBy(DEFAULT_GROUP_BY);
      setSubfilter(null);
    }
  }, [stage, groupBy]);

  const { groupByFromApi, isAggregate, rows, pinnedRows: apiPinnedRows, columns } = useMemo(
    () => buildNegotiatorGridModel(results, stage, groupBy),
    [results, stage, groupBy],
  );

  const drillDownBreadcrumbs = useMemo(
    () => buildDrillDownBreadcrumbs({ groupBy, subfilter }),
    [groupBy, subfilter],
  );

  const columnFieldsSignature = useMemo(
    () => columns.map((c) => c.field).sort().join('|'),
    [columns],
  );

  const allowedColumnFieldSet = useMemo(
    () => new Set(columns.map((c) => c.field)),
    [columns],
  );

  useEffect(() => {
    if (isAggregate) return;
    setColumnVisibilityModel((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of INDIVIDUAL_ONLY_HIDDEN_COLUMNS) {
        if (allowedColumnFieldSet.has(field) && !(field in next)) {
          next[field] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isAggregate, columnFieldsSignature, allowedColumnFieldSet]);

  const [gridFilterModel, setGridFilterModel] = useState(EMPTY_GRID_FILTER_MODEL);

  const safeGridFilterModel = useMemo(
    () => getSafeGridFilterModel(gridFilterModel, columns),
    [gridFilterModel, columns],
  );

  const [pinnedRows, setPinnedRows] = useState(undefined);

  useEffect(() => {
    if (gridFilterIsActive(safeGridFilterModel)) return;
    setPinnedRows(apiPinnedRows);
  }, [apiPinnedRows, safeGridFilterModel]);

  const handleGridFilterModelChange = useCallback(
    (model) => {
      const nextModel = getSafeGridFilterModel(model, columns);
      setGridFilterModel(nextModel);

      if (!isAggregate || !results?.totals) return;

      setTimeout(() => {
        const api = apiRef.current;
        if (!api) return;

        if (!gridFilterIsActive(nextModel)) {
          setPinnedRows(buildAggregatePinnedRows(results));
          return;
        }

        const filteredRows = getFilteredRowsAll(api);
        if (!filteredRows.length) {
          setPinnedRows(undefined);
          return;
        }

        setPinnedRows({
          bottom: [finalizeAggregateTotals(computeAggregateTotalsFromRows(filteredRows))],
        });
      }, 50);
    },
    [columns, apiRef, isAggregate, results],
  );

  useEffect(() => {
    setGridFilterModel((prev) => {
      const next = getSafeGridFilterModel(prev, columns);
      const prevItems = prev.items || [];
      const nextItems = next.items || [];
      const prevQuick = prev.quickFilterValues || [];
      const nextQuick = next.quickFilterValues || [];
      if (
        prevItems.length === nextItems.length
        && prevQuick.length === nextQuick.length
        && prevItems.every((item, i) => (
          item.field === nextItems[i]?.field && item.operator === nextItems[i]?.operator
        ))
      ) {
        return prev;
      }
      return next;
    });
  }, [columns, columnFieldsSignature]);

  useEffect(() => {
    if (!isAggregate || !results?.totals || !gridFilterIsActive(safeGridFilterModel)) return undefined;

    const frame = requestAnimationFrame(() => {
      const api = apiRef.current;
      if (!api) return;

      const filteredRows = getFilteredRowsAll(api);
      if (!filteredRows.length) {
        setPinnedRows(undefined);
        return;
      }

      setPinnedRows({
        bottom: [finalizeAggregateTotals(computeAggregateTotalsFromRows(filteredRows))],
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [apiRef, isAggregate, results, rows, safeGridFilterModel]);

  const addAnalysisCustomFilter = useCallback(() => {
    setAnalysisCustomFilters((prev) => [...prev, newNegotiatorAnalysisCustomFilterRow()]);
  }, []);

  const updateAnalysisCustomFilter = useCallback((id, patch) => {
    setAnalysisCustomFilters((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeAnalysisCustomFilter = useCallback((id) => {
    setAnalysisCustomFilters((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length === 0 ? [newNegotiatorAnalysisCustomFilterRow()] : next;
    });
  }, []);

  const removeAllAnalysisCustomFilters = useCallback(() => {
    setAnalysisCustomFilters([newNegotiatorAnalysisCustomFilterRow()]);
  }, []);

  const clearCustomFilters = useCallback(() => {
    setAnalysisCustomFilters([newNegotiatorAnalysisCustomFilterRow()]);
  }, []);

  const navigateToDrillDepth = useCallback((depth) => {
    if (!subfilter?.length) return;
    if (depth <= 0) {
      const first = subfilter[0].groupBy;
      if (DRILL_DOWN_TO_MUNICIPALITY_GROUP_BY.has(first)) {
        setGroupBy(first);
      }
      setSubfilter(null);
      return;
    }
    if (depth >= subfilter.length) return;
    setSubfilter(subfilter.slice(0, depth));
  }, [subfilter]);

  const handleGroupByChange = useCallback((value) => {
    setSubfilter(null);
    setGroupBy(value);
  }, []);

  const handleCellClick = useCallback((params) => {
    if (params.field !== 'ParcelPrefix' || params.id === 'totals') return;
    const code = params.row.group_code;
    if (code == null) return;
    if (DRILL_DOWN_TO_MUNICIPALITY_GROUP_BY.has(groupByFromApi)) {
      setGroupBy('municipality');
      setSubfilter([{ groupBy: groupByFromApi, value: code }]);
      return;
    }
    setSubfilter((prev) => (prev?.length ? [...prev, { groupBy: groupByFromApi, value: code }] : [{ groupBy: groupByFromApi, value: code }]));
  }, [groupByFromApi]);

  const negotiatorTogglableColumns = useCallback(
    (columnList) =>
      columnList
        .filter((col) => !COLUMNS_PANEL_EXCLUDED_FIELDS.includes(col.field))
        .map((col) => col.field),
    [],
  );

  return (
    <Box sx={{ p: 0, height: '100%', width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          apiRef={apiRef}
          key={columnFieldsSignature}
          rows={rows}
          columns={columns}
          loading={loading}
          hideFooter
          density="compact"
          disableRowSelectionOnClick
          pinnedRows={pinnedRows}
          onCellClick={handleCellClick}
          filterModel={safeGridFilterModel}
          onFilterModelChange={handleGridFilterModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
          slots={NEGOTIATOR_GRID_SLOTS}
          slotProps={{
            baseTooltip: {
              arrow: true,
              enterDelay: 400,
              slotProps: {
                tooltip: {
                  sx: {
                    maxWidth: 440,
                    whiteSpace: 'pre-line',
                    typography: 'body2',
                    lineHeight: 1.35,
                    py: 0.75,
                  },
                },
              },
            },
            columnsManagement: {
              getTogglableColumns: negotiatorTogglableColumns,
            },
            filterPanel: {
              analysisCustomFilters,
              onAddAnalysisCustomFilter: addAnalysisCustomFilter,
              onUpdateAnalysisCustomFilter: updateAnalysisCustomFilter,
              onRemoveAnalysisCustomFilter: removeAnalysisCustomFilter,
              onRemoveAllAnalysisCustomFilters: removeAllAnalysisCustomFilters,
            },
            toolbar: {
              stage,
              onStageChange: setStage,
              groupBy,
              onGroupByChange: handleGroupByChange,
              drillDownBreadcrumbs,
              onNavigateDrillDown: navigateToDrillDepth,
              onClearCustomFilters: clearCustomFilters,
              showClearCustomFilters: !subfilter?.length && negotiatorAnalysisCustomFiltersNeedToolbarClear(analysisCustomFilters),
              yearOptions,
              selectedYear: analysisYear,
              onYearChange: setAnalysisYear,
            },
          }}
          sx={{
            border: 'none',
            '& .clickable-cell': {
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
                backgroundColor: 'action.hover',
              },
            },
            '& .rev-metric-cell': {
              fontWeight: 600,
            },
            '& .result-won-cell': {
              color: '#1b5e20',
              fontWeight: 600,
            },
            '& .result-lost-cell': {
              color: '#c62828',
              fontWeight: 600,
            },
            '& .MuiDataGrid-pinnedRows--bottom': {
              '& .MuiDataGrid-row': {
                '& .MuiDataGrid-cell': {
                  fontWeight: 600,
                  backgroundColor: 'grey.200',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                },
              },
            },
            '& .MuiDataGrid-main': {
              width: 0,
              minWidth: '100%',
              flex: 1,
              minHeight: 0,
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto',
            },
            width: '100%',
            minWidth: 0,
            flex: 1,
            minHeight: 0,
          }}
          showToolbar />
      </Box>
    </Box>
  );
}

export default NegotiatorAnalysisRebuilt;
